import React, { useState } from 'react';
import axios from 'axios';
import { Lock, Check } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Buyer-facing CTA shown on PropertyDetailPage. Lets a logged-in buyer
 * tap a button to flag they'd want to be told if the property ever
 * becomes available off-market. Pushes to POST /api/properties/{id}
 * /off-market-signal. Idempotent — clicking again silently updates
 * an existing signal instead of duplicating it. Hides itself if the
 * visitor isn't logged in (login modal would interrupt; we just show
 * nothing rather than nag them).
 */
const OffMarketInterestButton = ({ propertyId }) => {
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const token = (typeof window !== 'undefined') ? localStorage.getItem('token') : null;
  if (!token || !propertyId) return null;

  const onClick = async () => {
    setSending(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/properties/${propertyId}/off-market-signal`,
        { note: null },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSubmitted(true);
      toast({
        title: '🔒 We\u2019ve flagged your interest',
        description: 'The agent can see you as an interested buyer if this comes back available.',
      });
    } catch (err) {
      toast({
        title: 'Could not flag interest',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Button
      type="button"
      variant="outline"
      onClick={onClick}
      disabled={sending || submitted}
      className={`mt-3 w-full justify-center text-sm rounded-lg ${
        submitted
          ? 'bg-emerald-50 border-emerald-200 text-emerald-800 hover:bg-emerald-50'
          : 'bg-amber-50 border-amber-200 text-amber-900 hover:bg-amber-100'
      }`}
      data-testid="off-market-interest-btn"
    >
      {submitted ? (
        <>
          <Check className="h-3.5 w-3.5 mr-1.5" />
          You&apos;ll hear from the agent first if this comes back
        </>
      ) : (
        <>
          <Lock className="h-3.5 w-3.5 mr-1.5" />
          {sending ? 'Saving…' : 'Notify me if this comes off-market'}
        </>
      )}
    </Button>
  );
};

export default OffMarketInterestButton;
